// src/components/chat/PatientSearchPicker.jsx
//
// Type-ahead patient picker for navigators starting a new PATIENT_NAVIGATOR
// conversation. NOT the same shape as NewConversationPicker.jsx (flat list)
// — a navigator's patient set is unrestricted and can run into the hundreds
// (per the hiring-ratio docs: "hire next navigator at 140 families"), so a
// flat dropdown doesn't scale the way it does for every other pairing in
// this chat feature (doctors/navigators are small, bounded sets).
//
// Reuses the EXISTING GET /api/patients/search endpoint (already built,
// already used by PatientListPage.js) rather than inventing new backend
// search logic — this component is purely a different frontend path onto
// data that was already searchable.

import React, { useState, useEffect, useRef } from 'react';
import { patientAPI } from '../../services/api';
import useDialogFocus from '../../hooks/useDialogFocus';

function initials(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

export default function PatientSearchPicker({ onSelect, onClose, title = 'Message a patient' }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(null);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);
  const dialogRef = useRef(null);
  useDialogFocus(dialogRef);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      return;
    }
    debounceRef.current = setTimeout(() => {
      setLoading(true);
      setError(null);
      patientAPI.list({ search: query.trim(), size: 15 })
        .then((res) => {
          const patients = res.data?.patients;
          // Defensive: this exact shape mismatch (a non-array reaching
          // results.map() below, with no ErrorBoundary on this page) already
          // crashed the whole app once — guard against it recurring for any
          // future response-shape change, not just this one.
          setResults(Array.isArray(patients) ? patients : []);
        })
        .catch((e) => setError(e.message || 'Search failed.'))
        .finally(() => setLoading(false));
    }, 300); // debounced — avoids firing a request on every keystroke
    return () => clearTimeout(debounceRef.current);
  }, [query]);

  async function handlePick(patient) {
    setStarting(patient.id);
    try {
      await onSelect({ id: patient.id, name: `${patient.firstName} ${patient.lastName}` });
    } finally {
      setStarting(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      className="dialog-backdrop"
      onClick={onClose}
    >
      <div className="dialog dialog--picker" ref={dialogRef} onClick={(e) => e.stopPropagation()}>
        <div className="picker-head">
          <div className="picker-title">{title}</div>
          <button type="button" className="picker-close" onClick={onClose} aria-label="Close">
            ✕
          </button>
        </div>

        <div className="picker-search">
          <input
            ref={inputRef}
            type="text"
            className="form-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or phone…"
            aria-label="Search patients"
          />
        </div>

        <div className="picker-results">
          {!query.trim() && (
            <div className="picker-msg">Start typing a patient's name or phone number.</div>
          )}
          {loading && <div className="picker-msg">Searching…</div>}
          {error && <div className="picker-msg picker-msg--error">{error}</div>}
          {!loading && !error && query.trim() && results.length === 0 && (
            <div className="picker-msg">No patients found for "{query.trim()}".</div>
          )}

          {results.map((p) => (
            <button
              key={p.id}
              type="button"
              className="picker-row"
              onClick={() => handlePick(p)}
              disabled={starting === p.id}
            >
              <div className="avatar avatar--sm">
                {initials(`${p.firstName} ${p.lastName}`)}
              </div>
              <div className="picker-row__body">
                <div className="picker-row__name">
                  {p.firstName} {p.lastName}
                </div>
                {p.phone && <div className="picker-muted">{p.phone}</div>}
              </div>
              {starting === p.id && <span className="picker-muted">⏳</span>}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
